import { Box, SxProps } from '@mui/material';
import React, { FC, useEffect, PropsWithChildren, useState } from 'react';
import { mergeSx } from '@/utils/mergeSx';
import { Api } from '@/services/Api';
import { Owner } from '@/declarations/models/Owner';
import { DashboardStatistics } from '@/declarations/models/DashboardStatistics';
import { TotalCountWidget } from '@/views/AdminView/widgets/TotalCountWidget';
import { formatNumberWithSpaces } from '@/utils/formatNumbersWithSpaces';
import { TotalCountMediaObjects } from '@/views/AdminView/widgets/TotalCountMediaObjects';

const DashboardGrid: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'auto repeat(auto-fill, 150px)',
        gridGap: '24px',
        width: '100%',
      }}>
      {children}
    </Box>
  );
};

const GridItem: FC<
  PropsWithChildren<{
    sx?: SxProps;
  }>
> = ({ children, sx }) => {
  return (
    <Box
      sx={mergeSx(
        {
          height: '100%',
          // border: `1px solid red`,
        },
        sx ?? {},
      )}>
      {children}
    </Box>
  );
};

export const AdminDashboard = () => {
  const [owners, setOwners] = useState<Array<Owner>>([]);
  const [dashboardStatistics, setDashboardStatistics] = useState<DashboardStatistics | null>(null);
  const totalSites = owners?.reduce((acc, owner) => acc + (owner.sites?.length || 0), 0);

  useEffect(() => {
    Api.getOwners({ rows: 500 })
      .fetch()
      .then((data) => {
        setOwners(data[0]?.owners || []);
      });
  }, []);

  useEffect(() => {
    Api.getDashboardStatistics()
      .fetch()
      .then((data) => {
        setDashboardStatistics(data[0] ?? null);
      });
  }, []);

  return (
    <DashboardGrid>
      <GridItem>
        <TotalCountWidget title={`${formatNumberWithSpaces(owners.length) || 'x'} museer`} />
      </GridItem>
      <GridItem>
        <TotalCountWidget title={`${formatNumberWithSpaces(totalSites) || 'x'} nettsteder`} />
      </GridItem>
      <GridItem>
        <TotalCountWidget title='XXX redaksjonelle brukere' />
      </GridItem>

      <GridItem>
        <TotalCountWidget
          title={`${formatNumberWithSpaces(dashboardStatistics?.total_count_pages?.total_count) || 'x'} sider`}
          description={`Nye i dag: ${
            formatNumberWithSpaces(dashboardStatistics?.total_count_pages?.new_today_count) ?? 'x'
          }`}
        />
      </GridItem>
      <GridItem>
        <TotalCountWidget title={`${formatNumberWithSpaces(dashboardStatistics?.total_count_cards) || 'x'} kort`} />
      </GridItem>
      <GridItem>
        <TotalCountWidget
          title={`${formatNumberWithSpaces(dashboardStatistics?.total_count_employees) || 'x'} ansatte`}
        />
      </GridItem>
      <GridItem>
        <TotalCountMediaObjects
          title={`${
            formatNumberWithSpaces(dashboardStatistics?.total_count_media_elements?.total) || 'x'
          } mediaelementer`}
          counts={{
            image: dashboardStatistics?.total_count_media_elements?.image ?? 0,
            audio: dashboardStatistics?.total_count_media_elements?.audio ?? 0,
            video: dashboardStatistics?.total_count_media_elements?.video ?? 0,
            document: dashboardStatistics?.total_count_media_elements?.document ?? 0,
          }}
        />
      </GridItem>
      <GridItem>
        <TotalCountWidget
          title={`${formatNumberWithSpaces(dashboardStatistics?.total_count_schemas) || 'x'} skjemaer`}
        />
      </GridItem>
      <GridItem>
        <TotalCountWidget title='Ideer:'>
          <ul>
            <li>Søk etter EMS ider globalt</li>
            <li>Søk etter slettede sider med mulighet for gjenoppretting?</li>
            <li>Redaksjonell statistikk (highscores)</li>
            <li>Graf over nettstedsopprettelse over tid</li>
            <li>Innganger til Kibana/Logger</li>
          </ul>
        </TotalCountWidget>
      </GridItem>
    </DashboardGrid>
  );
};
